import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import ClientReleasesView from './views/ClientReleasesView.vue';
import type { AdminPermission } from 'shared';
import LoginView from './views/LoginView.vue';
import AdminLayout from './layouts/AdminLayout.vue';
import ConsoleView from './views/ConsoleView.vue';
import AdminResourceView from './views/AdminResourceView.vue';
import CommerceProductsView from './views/CommerceProductsView.vue';
import GrowthPoliciesView from './views/GrowthPoliciesView.vue';
import SystemModelsView from './views/SystemModelsView.vue';
import SystemSettingsView from './views/SystemSettingsView.vue';
import CmsContentView from './views/CmsContentView.vue';
import PaymentSettingsView from './views/PaymentSettingsView.vue';
import CreativeMediaSettingsView from './views/CreativeMediaSettingsView.vue';
import RuntimeMonitorView from './views/RuntimeMonitorView.vue';
import MarketplaceOperationsView from './views/MarketplaceOperationsView.vue';
import CouponCampaignsView from './views/CouponCampaignsView.vue';
import MessageReviewsView from './views/MessageReviewsView.vue';
import AdvertisingOperationsView from './views/AdvertisingOperationsView.vue';
import { useAdminSessionStore } from './stores/admin-session.store';
import { adminNavigation } from './navigation';

declare module 'vue-router' {
  interface RouteMeta {
    permission?: AdminPermission;
    hidePageTitle?: boolean;
  }
}

const resource = (path: string, name: string, permission: AdminPermission): RouteRecordRaw => ({
  path,
  name,
  component: AdminResourceView,
  meta: { permission },
});
const marketplace = (
  path: string,
  name: string,
  tab: 'reviews' | 'assets' | 'curation' | 'analytics',
  permission: AdminPermission,
): RouteRecordRaw => ({
  path,
  name,
  component: MarketplaceOperationsView,
  props: { initialTab: tab },
  meta: { permission },
});

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/login', name: 'admin-login', component: LoginView },
    {
      path: '/',
      component: AdminLayout,
      meta: { permission: 'dashboard.read' },
      children: [
        {
          path: '',
          name: 'admin-dashboard',
          component: ConsoleView,
          meta: { permission: 'dashboard.read' },
        },
        resource('users', 'admin-users', 'user.read'),
        resource('workspaces', 'admin-workspaces', 'workspace.read'),
        marketplace(
          'marketplace/reviews',
          'admin-marketplace-reviews',
          'reviews',
          'marketplace.review',
        ),
        marketplace('marketplace/assets', 'admin-marketplace-assets', 'assets', 'marketplace.read'),
        marketplace(
          'marketplace/collections',
          'admin-marketplace-collections',
          'curation',
          'marketplace.curate',
        ),
        marketplace(
          'marketplace/analytics',
          'admin-marketplace-analytics',
          'analytics',
          'marketplace.read',
        ),
        {
          path: 'commerce/products',
          name: 'admin-products',
          component: CommerceProductsView,
          meta: { permission: 'commerce.read' },
        },
        {
          path: 'commerce/coupons',
          name: 'admin-coupons',
          component: CouponCampaignsView,
          meta: { permission: 'commerce.read' },
        },
        resource('commerce/orders', 'admin-orders', 'commerce.read'),
        resource('commerce/refunds', 'admin-refunds', 'commerce.read'),
        resource('commerce/settlements', 'admin-settlements', 'settlement.manage'),
        resource('commerce/payment-attempts', 'admin-payment-attempts', 'commerce.read'),
        {
          path: 'commerce/payment-settings',
          name: 'admin-payment-settings',
          component: PaymentSettingsView,
          meta: { permission: 'commerce.read' },
        },
        resource('commerce/subscriptions', 'admin-subscriptions', 'commerce.read'),
        {
          path: 'monitor',
          name: 'admin-monitor',
          component: RuntimeMonitorView,
          meta: { permission: 'dashboard.read' },
        },
        {
          path: 'system-services',
          name: 'admin-system-services',
          component: RuntimeMonitorView,
          props: { serviceDiagnostics: true },
          meta: { permission: 'dashboard.read' },
        },
        {
          path: 'message-reviews',
          name: 'admin-message-reviews',
          component: MessageReviewsView,
          meta: { permission: 'message_review.read' },
        },
        resource('notifications', 'admin-notifications', 'dashboard.read'),
        resource('audits', 'admin-audits', 'audit.read'),
        resource('admins', 'admin-admins', 'admin.manage'),
        {
          path: 'system-models',
          name: 'admin-system-models',
          component: SystemModelsView,
          meta: { permission: 'settings.read' },
        },
        {
          path: 'growth',
          name: 'admin-growth',
          component: GrowthPoliciesView,
          meta: { permission: 'settings.read' },
        },
        {
          path: 'creative-media',
          name: 'admin-creative-media',
          component: CreativeMediaSettingsView,
          meta: { permission: 'settings.read' },
        },
        {
          path: 'advertising',
          name: 'admin-advertising',
          component: AdvertisingOperationsView,
          meta: { permission: 'advertising.read' },
        },
        {
          path: 'settings',
          name: 'admin-settings',
          component: SystemSettingsView,
          meta: { permission: 'settings.read' },
        },
        {
          path: 'client-releases',
          name: 'admin-client-releases',
          component: ClientReleasesView,
          meta: { permission: 'settings.read' },
        },
        {
          path: 'content',
          name: 'admin-cms-content',
          component: CmsContentView,
          meta: { permission: 'cms.read', hidePageTitle: true },
        },
        { path: 'marketplace', redirect: { name: 'admin-marketplace-reviews' } },
        { path: 'orders', redirect: { name: 'admin-orders' } },
        { path: 'refunds', redirect: { name: 'admin-refunds' } },
        { path: 'settlements', redirect: { name: 'admin-settlements' } },
        { path: 'payment-attempts', redirect: { name: 'admin-payment-attempts' } },
        { path: 'publications', redirect: { name: 'admin-marketplace-assets' } },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: { name: 'admin-dashboard' } },
  ],
});

router.beforeEach(async (to) => {
  if (to.name === 'admin-login') {
    if (sessionStorage.getItem('adminAccessToken')) return { name: 'admin-dashboard' };
    return true;
  }
  if (!sessionStorage.getItem('adminAccessToken'))
    return { name: 'admin-login', query: { redirect: to.fullPath } };
  const session = useAdminSessionStore();
  const profile = await session.ensureProfile();
  if (!profile) return { name: 'admin-login', query: { redirect: to.fullPath } };
  if (to.meta.permission && !session.can(to.meta.permission)) return { name: 'admin-dashboard' };
  return true;
});

router.afterEach((to) => {
  const label =
    to.name === 'admin-login'
      ? '管理员登录'
      : to.name === 'admin-dashboard'
        ? '数据看板'
        : (adminNavigation
            .flatMap((group) => group.children)
            .find((item) => item.routeName === to.name)?.label ?? '管理后台');
  document.title = `${label} · 灵犀工坊`;
});

export default router;
